/**
 * Shared bits for the support UI: status/priority styling, an "unconfigured"
 * panel for the 503 state, and a small hook that resolves the current admin's
 * support access (GET /support/access) so the list/detail/new pages can gate
 * the submit affordance and surface a graceful disabled state.
 */
import { ApiError } from '@fc/core'
import { Badge, Card } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { LifeBuoy } from 'lucide-react'
import { PageHeader } from '../../components/page-header'
import { getSupportAccess, type SupportAccess, type TicketPriority } from '../../lib/support-api'

/** Map a ticket status to a Badge variant. Unknown statuses fall back to neutral. */
export function statusVariant(status: string): 'success' | 'neutral' | 'warning' | 'danger' {
  const s = status.toLowerCase()
  if (s === 'open' || s === 'new') return 'warning'
  if (s === 'pending' || s === 'in_progress' || s === 'in-progress') return 'neutral'
  if (s === 'resolved' || s === 'closed' || s === 'done') return 'success'
  return 'neutral'
}

/** Map a ticket priority to a Badge variant. */
export function priorityVariant(
  priority: TicketPriority | string,
): 'success' | 'neutral' | 'warning' | 'danger' {
  const p = priority.toLowerCase()
  if (p === 'urgent') return 'danger'
  if (p === 'high') return 'warning'
  if (p === 'low') return 'neutral'
  return 'neutral' // normal
}

/** Title-case a snake/kebab status or priority for display. */
export function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

/** `true` when the error means support is not configured on this server. */
export function isUnconfigured(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'support_unconfigured'
}

/**
 * Resolve the current admin's support access. The query is allowed to fail
 * (e.g. 503 when EMS is unconfigured); callers inspect `error` via
 * {@link isUnconfigured} to render the right empty state.
 */
export function useSupportAccess() {
  return useQuery<SupportAccess>({
    queryKey: ['support-access'],
    queryFn: getSupportAccess,
    retry: false,
  })
}

/** Friendly panel shown when EMS support is not configured (503). */
export function SupportUnconfigured() {
  return (
    <Card>
      <div className="space-y-3 py-20 text-center">
        <LifeBuoy className="mx-auto size-8 text-dim" aria-hidden="true" />
        <p className="text-sm text-muted">Support isn’t available on this server yet.</p>
        <p className="mx-auto max-w-sm text-xs text-dim">
          The support service hasn’t been configured. Please contact your platform administrator.
        </p>
      </div>
    </Card>
  )
}

/** Heading reused across the support pages. */
export function SupportHeader({ actions }: { actions?: React.ReactNode }) {
  return (
    <PageHeader
      title="Support"
      description="Submit and track support requests with the FOSS Church team."
      actions={actions}
    />
  )
}
